import { Link } from "react-router-dom";
import { ArrowRight, BellRing, FileCheck2, LogIn, Mail, MessageSquare, PlayCircle, ShieldCheck, Smartphone, Stethoscope, UserPlus, Zap } from "lucide-react";
import { KonsildentLogo, Footer } from "../components/Brand";

// Später ersetzen: z. B. "https://www.youtube-nocookie.com/embed/VIDEO_ID"
const VIDEO_URL = "";

const NAV: [string, string][] = [["vorteile", "Vorteile"], ["ablauf", "So funktioniert es"], ["video", "Video"], ["preise", "Preise"], ["kontakt", "Kontakt"]];

const VORTEILE = [
  { icon: Zap, t: "Überweisung in 2 Minuten", d: "Strukturiertes Formular statt Fax und Telefon – vom Handy, Tablet oder PC." },
  { icon: FileCheck2, t: "Vollständige Unterlagen", d: "Röntgenbilder, Befunde und Angaben kommen geprüft und vollständig in der Fachpraxis an." },
  { icon: BellRing, t: "Status jederzeit sichtbar", d: "Die überweisende Praxis sieht, ob der Patient kontaktiert, behandelt oder der Bericht versendet ist." },
  { icon: MessageSquare, t: "Direkter Austausch", d: "Rückfragen und Nachrichten zum Fall an einem Ort – ohne Telefonschleifen." },
  { icon: ShieldCheck, t: "Datenschutz im Fokus", d: "Zwei-Faktor-Anmeldung, Audit-Protokoll und verschlüsselte Übertragung." },
  { icon: Smartphone, t: "Als App installierbar", d: "Konsildent läuft im Browser und lässt sich auf dem Handy wie eine App ablegen." },
];

const SCHRITTE = [
  { n: "1", t: "Überweisen", d: "Die Hauszahnarztpraxis füllt das Online-Formular aus und lädt Bilder hoch." },
  { n: "2", t: "Behandeln", d: "Die Fachpraxis prüft den Fall, kontaktiert den Patienten und vereinbart den Termin." },
  { n: "3", t: "Berichten", d: "Der Behandlungsbericht geht digital an die überweisende Praxis zurück." },
];

const PREISE = [
  { t: "Überweiserpraxis", p: "[Preis]", d: "Für Hauszahnarztpraxen, die Patienten überweisen.", f: ["Unbegrenzte Überweisungen", "Statusverfolgung", "Team-Zugänge"] },
  { t: "Fachpraxis", p: "[Preis]", d: "Für Oralchirurgie, Implantologie und MKG.", f: ["Fallverwaltung & Berichte", "Eigenes Überweiserportal", "Audit-Protokoll"], hi: true },
  { t: "Praxisgruppe", p: "[Preis]", d: "Für mehrere Standorte und größere Teams.", f: ["Alles aus Fachpraxis", "Mehrere Standorte", "Persönliche Einrichtung"] },
];

function Header() {
  return (
    <header className="sticky top-0 z-30 border-b border-border bg-surface/90 backdrop-blur-md">
      <div className="mx-auto flex max-w-6xl items-center justify-between gap-4 px-4 py-3">
        <Link to="/" aria-label="Startseite" data-testid="landing-logo"><KonsildentLogo className="h-9 sm:h-10" /></Link>
        <nav className="hidden items-center gap-6 lg:flex" aria-label="Hauptnavigation">
          {NAV.map(([id, l]) => <a key={id} href={`#${id}`} className="text-sm text-text-muted no-underline hover:text-text" data-testid={`landing-nav-${id}`}>{l}</a>)}
        </nav>
        <Link to="/login" className="inline-flex min-h-[44px] items-center gap-2 rounded-lg bg-primary px-4 font-medium text-white no-underline transition-colors hover:bg-primary-dark" data-testid="landing-header-login">
          <LogIn className="h-4 w-4" aria-hidden />Anmelden
        </Link>
      </div>
    </header>
  );
}

function Hero() {
  return (
    <section className="mx-auto grid max-w-6xl items-center gap-10 px-4 py-14 lg:grid-cols-[1.1fr_1fr] lg:py-20">
      <div className="kd-fade">
        <p className="mb-4 inline-flex items-center gap-2 rounded-full bg-success-bg px-3 py-1 text-sm font-medium text-accent-text"><Stethoscope className="h-4 w-4" aria-hidden />Das Überweiserportal für Zahnarztpraxen</p>
        <h1 className="mb-5 text-4xl font-semibold leading-tight tracking-tight sm:text-5xl lg:text-6xl" data-testid="landing-title">Überweisen. Verbinden. <span className="text-primary">Versorgen.</span></h1>
        <p className="mb-8 max-w-xl text-base text-text-muted md:text-lg">Konsildent verbindet Hauszahnarztpraxen und Fachpraxen digital – von der Überweisung über die Terminvergabe bis zum Behandlungsbericht.</p>
        <div className="flex flex-col gap-3 sm:flex-row">
          <Link to="/registrieren" className="inline-flex min-h-[48px] items-center justify-center gap-2 rounded-lg bg-primary px-5 font-medium text-white no-underline transition-colors hover:bg-primary-dark" data-testid="landing-hero-register">
            <UserPlus className="h-5 w-5" aria-hidden />Kostenlos registrieren
          </Link>
          <a href="#video" className="inline-flex min-h-[48px] items-center justify-center gap-2 rounded-lg border border-border bg-surface px-5 font-medium text-text no-underline transition-colors hover:bg-bg" data-testid="landing-hero-video">
            <PlayCircle className="h-5 w-5 text-primary" aria-hidden />Demo ansehen
          </a>
        </div>
      </div>
      <div className="relative kd-fade">
        <img src="/img/praxis.jpg" alt="Moderne Zahnarztpraxis" className="aspect-[4/3] w-full rounded-2xl object-cover shadow-card" />
        <div className="absolute -bottom-5 left-4 right-4 rounded-xl border border-border bg-surface/95 p-4 shadow-card backdrop-blur sm:left-auto sm:right-[-12px] sm:w-64">
          <p className="text-sm text-text-muted">Fall UEB-2026-00042</p>
          <p className="font-medium text-success">Bericht versendet</p>
        </div>
      </div>
    </section>
  );
}

function Section({ id, title, sub, children, alt }: { id: string; title: string; sub?: string; children: React.ReactNode; alt?: boolean }) {
  return (
    <section id={id} className={`scroll-mt-20 py-16 lg:py-20 ${alt ? "bg-surface" : ""}`} data-testid={`landing-section-${id}`}>
      <div className="mx-auto max-w-6xl px-4">
        <h2 className="mb-2 text-2xl font-semibold sm:text-3xl">{title}</h2>
        {sub && <p className="mb-10 max-w-2xl text-text-muted">{sub}</p>}
        {children}
      </div>
    </section>
  );
}

function Vorteile() {
  return (
    <Section id="vorteile" title="Ihre Vorteile" sub="Weniger Telefonate, vollständige Unterlagen und volle Transparenz für beide Praxen." alt>
      <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
        {VORTEILE.map(({ icon: I, t, d }) => (
          <div key={t} className="rounded-xl border border-border bg-bg p-6 transition-transform duration-200 hover:-translate-y-1">
            <I className="mb-4 h-7 w-7 text-primary" aria-hidden />
            <h3 className="mb-2">{t}</h3>
            <p className="text-sm text-text-muted">{d}</p>
          </div>
        ))}
      </div>
    </Section>
  );
}

function Ablauf() {
  return (
    <Section id="ablauf" title="So funktioniert es" sub="Drei Schritte – für Überweiser und Fachpraxis gleichermaßen einfach.">
      <ol className="grid gap-6 md:grid-cols-3">
        {SCHRITTE.map((s) => (
          <li key={s.n} className="relative rounded-xl border border-border bg-surface p-6" data-testid={`landing-step-${s.n}`}>
            <span className="mb-4 flex h-11 w-11 items-center justify-center rounded-full bg-primary text-lg font-semibold text-white">{s.n}</span>
            <h3 className="mb-2">{s.t}</h3>
            <p className="text-sm text-text-muted">{s.d}</p>
          </li>
        ))}
      </ol>
    </Section>
  );
}

function Video() {
  return (
    <Section id="video" title="Konsildent in 2 Minuten" sub="Sehen Sie, wie eine Überweisung von der Hauszahnarztpraxis bis zum Bericht läuft." alt>
      <div className="overflow-hidden rounded-2xl border border-border bg-[#14232B]" data-testid="landing-video">
        {VIDEO_URL ? (
          <iframe src={VIDEO_URL} title="Konsildent Demo-Video" className="aspect-video w-full" allow="encrypted-media; picture-in-picture" allowFullScreen />
        ) : (
          <div className="flex aspect-video w-full flex-col items-center justify-center gap-3 text-white/80" data-testid="landing-video-placeholder">
            <PlayCircle className="h-16 w-16 text-accent-light" aria-hidden />
            <p className="text-base">[Demo-Video folgt]</p>
          </div>
        )}
      </div>
    </Section>
  );
}

function Preise() {
  return (
    <Section id="preise" title="Preise" sub="Transparente Pakete für jede Praxisgröße.">
      <div className="grid gap-6 md:grid-cols-3">
        {PREISE.map((p) => (
          <div key={p.t} className={`flex flex-col rounded-xl border bg-surface p-6 ${p.hi ? "border-primary shadow-card" : "border-border"}`} data-testid={`landing-price-${p.t}`}>
            <h3 className="mb-1">{p.t}</h3>
            <p className="mb-3 text-sm text-text-muted">{p.d}</p>
            <p className="mb-5 text-3xl font-semibold">{p.p}<span className="text-sm font-normal text-text-muted"> / Monat</span></p>
            <ul className="mb-6 space-y-2 text-sm">{p.f.map((f) => <li key={f} className="flex gap-2"><FileCheck2 className="h-4 w-4 shrink-0 text-accent" aria-hidden />{f}</li>)}</ul>
            <a href="#kontakt" className={`mt-auto inline-flex min-h-[44px] items-center justify-center gap-2 rounded-lg px-4 font-medium no-underline transition-colors ${p.hi ? "bg-primary text-white hover:bg-primary-dark" : "border border-border text-text hover:bg-bg"}`}>Anfragen<ArrowRight className="h-4 w-4" aria-hidden /></a>
          </div>
        ))}
      </div>
    </Section>
  );
}

function Kontakt() {
  return (
    <Section id="kontakt" title="Jetzt starten" alt>
      <div className="grid gap-6 md:grid-cols-3">
        <div className="rounded-xl border border-border bg-bg p-6">
          <UserPlus className="mb-3 h-7 w-7 text-primary" aria-hidden />
          <h3 className="mb-2">Überweiserpraxis</h3>
          <p className="mb-5 text-sm text-text-muted">Legen Sie Ihr Praxiskonto an und überweisen Sie sofort.</p>
          <Link to="/registrieren" className="font-medium" data-testid="landing-contact-register">Konto anlegen →</Link>
        </div>
        <div className="rounded-xl border border-border bg-bg p-6">
          <LogIn className="mb-3 h-7 w-7 text-primary" aria-hidden />
          <h3 className="mb-2">Bereits registriert?</h3>
          <p className="mb-5 text-sm text-text-muted">Anmeldung für Fachpraxen und Überweiserpraxen.</p>
          <Link to="/login" className="font-medium" data-testid="landing-contact-login">Zur Anmeldung →</Link>
        </div>
        <div className="rounded-xl border border-border bg-bg p-6">
          <Mail className="mb-3 h-7 w-7 text-primary" aria-hidden />
          <h3 className="mb-2">Fachpraxis werden</h3>
          <p className="mb-5 text-sm text-text-muted">Sie möchten Konsildent für Ihre Fachpraxis nutzen? Schreiben Sie uns.</p>
          <p className="font-medium" data-testid="landing-contact-email">[Kontakt-E-Mail]</p>
        </div>
      </div>
    </Section>
  );
}

export default function Landing() {
  return (
    <div className="min-h-screen bg-bg" data-testid="landing-page">
      <Header />
      <main>
        <Hero />
        <Vorteile />
        <Ablauf />
        <Video />
        <Preise />
        <Kontakt />
      </main>
      <Footer />
    </div>
  );
}
